import { useState, useRef, useCallback, useEffect } from 'react'
import { base64ToAudioBlob, extractTextForTTS } from 'utils/audioUtils'
import { getAuthToken } from 'lib/auth'

const TTS_URL = process.env.REACT_APP_ELEVENLABS_TTS_URL

export const useTextToSpeech = () => {
  const [isSupported] = useState<boolean>(!!TTS_URL)
  const [isLoading, setIsLoading] = useState(false)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)

  const objectUrlRef = useRef<string | null>(null)
  // Only the latest request may set the audio: a slower answer for an earlier message (or one
  // the user already stopped) is dropped.
  const requestIdRef = useRef(0)

  const clearAudio = useCallback(() => {
    requestIdRef.current += 1
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }
    setAudioUrl(null)
    setIsLoading(false)
  }, [])

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
      }
    }
  }, [])

  const fetchAudio = useCallback(
    async (text: string, languageCode?: string) => {
      if (!isSupported || !text) return

      clearAudio()
      const requestId = requestIdRef.current
      setIsLoading(true)

      try {
        const cleanText = extractTextForTTS(text)

        const authToken = await getAuthToken()
        const response = await fetch(TTS_URL!, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken && { Authorization: authToken }),
          },
          body: JSON.stringify({
            text: cleanText,
            ...(languageCode ? { languageCode } : {}),
          }),
        })

        if (!response.ok) {
          throw new Error(`TTS request failed: ${response.status}`)
        }

        const { audioBase64 } = await response.json()
        if (requestId !== requestIdRef.current) return
        const blob = base64ToAudioBlob(audioBase64, 'audio/mpeg')
        const url = URL.createObjectURL(blob)
        objectUrlRef.current = url
        setAudioUrl(url)
      } catch (err: any) {
        if (requestId === requestIdRef.current) setAudioUrl(null)
      } finally {
        if (requestId === requestIdRef.current) setIsLoading(false)
      }
    },
    [isSupported, clearAudio]
  )

  return {
    isSupported,
    isLoading,
    audioUrl,
    fetchAudio,
    clearAudio,
  }
}
